/**
 * Field navigation (ADR-0102 addendum 2): clicking a field-bound value in
 * the beta preview or in Anpassa text opens a small popover with
 * "Ändra i formuläret" and "Kommentera".
 *
 *   - Ändra i formuläret: in draft it opens the field; in pending /
 *     in_review it first confirms, returns the contract to draft
 *     (`revert_to_draft`, cancelling an open review and resetting an
 *     approval) and then opens the field.
 *   - Kommentera: a field request on the value; the contract stays where it is.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, tenantApi, type ContractDetail } from '../../api.js';
import { useInvalidate } from '../../query.js';
import { CommentComposer } from './ReviewThreads.js';
import { COMMENTABLE_STATUSES, formNavigationMode, returnToDraftConfirm } from './review-model.js';

export type FieldTarget = { key: string; attachmentId: string | null };

type Rect = { left: number; top: number; bottom: number; right: number };

const REVERT_ERRORS: Record<string, string> = {
  review_still_open: 'Granskningen pågår fortfarande. Avbryt granskningen först.',
  contract_not_pending: 'Avtalet kan inte gå tillbaka till utkast i sin nuvarande status.',
};

export function useFieldActions({
  contract,
  fieldLabel,
  onOpenField,
  onCommented,
}: {
  contract: Pick<ContractDetail, 'id' | 'status' | 'approval'> | null;
  fieldLabel?: (target: FieldTarget) => string | null;
  /** Opens the field in the form (after any return to draft). */
  onOpenField: (target: FieldTarget) => void;
  onCommented?: () => Promise<void> | void;
}): {
  open: (target: FieldTarget, rect: Rect) => void;
  /** "Ändra i formuläret" / "Gå till fältet" without the popover. */
  changeInForm: (target: FieldTarget) => Promise<void>;
  /** A failed return to draft, when no popover is open to show it. */
  error: string | null;
  clearError: () => void;
  element: ReactNode;
} {
  const invalidate = useInvalidate();
  const [state, setState] = useState<{ target: FieldTarget; rect: Rect; commenting: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    setState(null);
    setErr(null);
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [state, close]);

  const changeInForm = useCallback(
    async (target: FieldTarget) => {
      if (!contract) return;
      const mode = formNavigationMode(contract.status);
      if (mode === null) return;
      if (mode === 'confirm') {
        if (!window.confirm(returnToDraftConfirm(contract.status, !!contract.approval?.approved))) return;
        setBusy(true);
        setErr(null);
        try {
          await tenantApi.contracts.revert_to_draft({
            id: contract.id,
            cancel_open_review: contract.status === 'in_review',
            reset_approval: true,
          });
          await Promise.all([
            invalidate('contracts.get', contract.id),
            invalidate('reviews.list_for_contract', contract.id),
            invalidate('contracts.list'),
            invalidate('reviews.list_my_pending'),
          ]);
        } catch (e) {
          const m = e instanceof ApiError ? e.message : '';
          setErr(REVERT_ERRORS[m] ?? (e instanceof ApiError && e.status === 403 ? 'Du har inte behörighet att ändra avtalet.' : 'Avtalet kunde inte gå tillbaka till utkast.'));
          return;
        } finally {
          setBusy(false);
        }
      }
      setState(null);
      onOpenField(target);
    },
    [contract, invalidate, onOpenField],
  );

  const open = useCallback((target: FieldTarget, rect: Rect) => {
    setErr(null);
    setState({ target, rect, commenting: false });
  }, []);

  let element: ReactNode = null;
  if (state && contract) {
    const mode = formNavigationMode(contract.status);
    const canComment = COMMENTABLE_STATUSES.has(contract.status);
    const label = fieldLabel?.(state.target) ?? 'Uppgift från formuläret';
    const width = 288;
    const left = Math.max(8, Math.min(state.rect.left, window.innerWidth - width - 8));
    const below = state.rect.bottom + 6;
    const top = below + 200 > window.innerHeight ? Math.max(8, state.rect.top - 206) : below;
    element = (
      <div
        ref={boxRef}
        role="dialog"
        aria-label={label}
        style={{ position: 'fixed', left, top, width, zIndex: 60 }}
        className="rounded-md border border-stone-300 bg-white p-2 text-xs shadow-lg"
      >
        <div className="mb-1.5 flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate font-medium text-stone-900" title={label}>
            {label}
          </span>
          <button type="button" aria-label="Stäng" className="text-stone-400 hover:text-stone-700" onClick={close}>
            ×
          </button>
        </div>
        {state.commenting ? (
          <CommentComposer
            autoFocus
            placeholder="Vad ska ändras i uppgiften?"
            submitLabel="Kommentera"
            onCancel={() => setState({ ...state, commenting: false })}
            onSubmit={async (body) => {
              await tenantApi.review_comments.create({
                contract_id: contract.id,
                attachment_id: state.target.attachmentId,
                anchor_kind: 'field',
                anchor: state.target.key,
                kind: 'field_request',
                body,
              });
              close();
              await invalidate('review_comments.list', contract.id);
              await onCommented?.();
            }}
          />
        ) : (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              disabled={mode === null || busy}
              title={mode === null ? 'Uppgifterna kan inte ändras i avtalets nuvarande status.' : undefined}
              onClick={() => void changeInForm(state.target)}
              className="rounded px-2 py-1.5 text-left font-medium text-stone-900 hover:bg-stone-100 disabled:opacity-40"
            >
              Ändra i formuläret
              {mode === 'confirm' && <span className="block text-[11px] font-normal text-stone-500">Avtalet går tillbaka till utkast</span>}
            </button>
            <button
              type="button"
              disabled={!canComment}
              title={canComment ? undefined : 'Avtalet kan inte kommenteras i sin nuvarande status.'}
              onClick={() => setState({ ...state, commenting: true })}
              className="rounded px-2 py-1.5 text-left font-medium text-stone-900 hover:bg-stone-100 disabled:opacity-40"
            >
              Kommentera
              <span className="block text-[11px] font-normal text-stone-500">Begär en ändring av uppgiften</span>
            </button>
          </div>
        )}
        {err && <p className="mt-1.5 text-[11px] text-red-700">{err}</p>}
      </div>
    );
  }

  return { open, changeInForm, error: state ? null : err, clearError: () => setErr(null), element };
}
